import { ref, computed, type Ref } from 'vue';

export type HomeWagePayrollViewMode = 'admin' | 'director';

const STORAGE_PREFIX = 'homeWagePayrollViewPreview:';

/**
 * 방문요양 임금설정 — 관리자/센터장 표시 모드
 * - 관리자(60+): 기본 센터장 시점, [센터장 시점] 토글로 관리자 화면 전환
 * - 멤버 계정: 항상 센터장 시점
 * 계산·DB는 변경하지 않고 UI·출력물만 분기한다.
 */
export function useHomeWagePayrollViewMode(isAdminUser: Ref<boolean>) {
  const directorPreviewActive = ref(true);

  const isDirectorPayrollView = computed(
    () => !isAdminUser.value || directorPreviewActive.value
  );

  const canToggleDirectorPreview = computed(() => isAdminUser.value);

  const payrollViewMode = computed<HomeWagePayrollViewMode>(() =>
    isDirectorPayrollView.value ? 'director' : 'admin'
  );

  const loadDirectorPreview = (mCode: string) => {
    if (!isAdminUser.value) {
      directorPreviewActive.value = false;
      return;
    }
    if (!mCode) {
      directorPreviewActive.value = true;
      return;
    }
    try {
      const stored = sessionStorage.getItem(`${STORAGE_PREFIX}${mCode}`);
      // 저장값 없음 → 센터장 시점이 기본
      directorPreviewActive.value = stored === null ? true : stored === '1';
    } catch {
      directorPreviewActive.value = true;
    }
  };

  const persistDirectorPreview = (mCode: string) => {
    if (!isAdminUser.value || !mCode) return;
    try {
      sessionStorage.setItem(
        `${STORAGE_PREFIX}${mCode}`,
        directorPreviewActive.value ? '1' : '0'
      );
    } catch {
      /* ignore */
    }
  };

  const toggleDirectorPreview = (mCode?: string) => {
    if (!canToggleDirectorPreview.value) return;
    directorPreviewActive.value = !directorPreviewActive.value;
    if (mCode) persistDirectorPreview(mCode);
  };

  return {
    directorPreviewActive,
    isDirectorPayrollView,
    canToggleDirectorPreview,
    payrollViewMode,
    toggleDirectorPreview,
    loadDirectorPreview,
    persistDirectorPreview,
  };
};
